/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { css } from '@emotion/core'
import Tree from 'rc-tree'
import zionDebug from '@fs/zion-debug'
import {
  Autosuggest,
  Button,
  colors,
  DataBlock,
  H5,
  InfoSheet,
  Paragraph,
  Row,
  Separator,
  useInfoSheet,
  useZoomPan,
  Alert,
} from '@fs/zion-ui'
import { ContentDetach, MenuSearch, NoticeLoading, SocialShare, TreeViewRelationship } from '@fs/zion-icon'
import { SingleImageViewer, RecordHighlight, Highlights, useHighlights } from '@fs/zion-image-viewer'
import { useFeatureFlag } from '@fs/zion-flags'
import { useQueryParams } from '@fs/zion-router'
import 'rc-tree/assets/index.css'
import {
  getAllProcessedElements,
  getHighlights,
  getImage,
  getMostRecentFieldValue,
  getMostRecentFieldValueInArray,
  getNestedOrFloatingElementById,
  getNestedOrFloatingSubelementsById,
  getPersonName,
  getSlsActionsByElementId,
  handleSettingZoomPan,
  useSlsStateContext,
} from '@fs/zion-sls'
import { setDisplayHighlights } from '../../../../utils/imageHighlightUtils'
import ActionAndElementDetails from '../ActionAndElementDetails'
import { getImageArkUrl } from '../../../../../shared/services/das/dasService'
import DebugErrorNotice from '../DebugErrorNotice'
import useCollectionAlertBanner from '../../../../providers/collectionProvider/hooks/useCollectionAlertBanner'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'

const containerCss = css`
  .rc-tree-iconEle rc-tree-icon__customize {
    width: 18px;
  }
`
const imageContainerCSS = css`
  height: 200px;
  & > div {
    height: 200px;
  }
`

const debug = zionDebug('rip:debug:ElementTreeView')

// Keys must be unique in entire tree, so parent IDs are appended to show shared fields/subtrees in multiple places in the tree
// Example tree node keys: 'sharedFieldId||sharedParentId||grandparent1' and 'sharedFieldId||sharedParentId||grandparent2'
const treeKeySep = '||'

export default function ElementTreeView({ fsUserAgentChain, initialExpandElementId }) {
  const state = useSlsStateContext()
  const rawDataInfoSheet = useInfoSheet()
  const { setQuery } = useQueryParams()

  // eslint-disable-next-line no-warning-comments
  // TODO: switch this to a reducer so we don't have so many useStates
  const [groupedActions, setGroupedActions] = useState({})
  const [isExpanding, setIsExpanding] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState([])
  const [selectedKeys, setSelectedKeys] = useState([])
  const [rawData, setRawData] = useState()
  const [currentImage, setCurrentImage] = useState()
  const [imageHighlights, setImageHighlights] = useState()
  const [error, setError] = useState(null)
  const [selectedActions, setSelectedActions] = useState(null)
  const [highlightsState, highlightsApi] = useHighlights()

  const shared_dasProxy = useFeatureFlag('shared_dasProxy')
  const [imageViewer, { set: setZoomPan }] = useZoomPan()
  const hasScrolledToInitial = useRef(false)

  const { collectionId } = useCollectionContextProvider()
  const { alertBannerProps: collectionBannerProps, showBanner: collectionShowBanner } = useCollectionAlertBanner({
    collectionId,
  })

  useEffect(() => {
    if (state.images && state.currentImageArk) {
      setCurrentImage(getImage(state.images, state.currentImageArk))
    }
  }, [state.currentImageArk, state.images])
  const autosuggestElements = useMemo(() => {
    const allProcessedElements = currentImage ? getAllProcessedElements([currentImage]) : []
    return allProcessedElements?.map((element) => ({ key: element.id, primaryText: element.id }))
  }, [currentImage])

  const autosuggestValues = useMemo(() => {
    const allProcessedElements = currentImage ? getAllProcessedElements([currentImage]) : []
    const fields = []
    allProcessedElements.forEach((element) => {
      const value = getMostRecentFieldValue(element)
      if (element.elementType === 'FIELD' && value?.trim() !== '') {
        fields.push({ key: element.id, primaryText: getMostRecentFieldValue(element) || '' })
      }
    })
    return fields
  }, [currentImage])

  // eslint-disable-next-line no-warning-comments
  // TODO: this is being renamed in a current PR Patrick is working on
  // Starts from the selected node and finds the id of each parent node until reaching the top of the tree
  const findElementPath = useCallback(
    (elementId) => {
      let currentNode = getNestedOrFloatingElementById([currentImage], elementId)
      if (!currentNode) {
        console.warn('Could not find element in current image', elementId)
        setError(new Error(`Could not find element with ID: ${elementId}`))
        return [] // Return an empty array to indicate no path found
      }
      const allKeys = [elementId]
      while (
        currentNode?.superElements &&
        currentNode?.superElements?.find((se) => se.partitionKey === state.currentImageArk)
      ) {
        const superElement = currentNode?.superElements?.find((se) => se.partitionKey === state.currentImageArk)
        allKeys.push(superElement.id)
        currentNode = getNestedOrFloatingElementById([currentImage, {}] /* needs to be an array */, superElement.id)
      }
      return allKeys
    },
    [currentImage, setError, state.currentImageArk]
  )
  // Gets the element represented by the node and strips the subs and superElements arrays from it
  const getRawData = useCallback(
    (elementId) => {
      const selectedElement =
        getNestedOrFloatingElementById(currentImage.subElements, elementId) ||
        getNestedOrFloatingElementById(currentImage.floatingElements, elementId)

      if (!selectedElement) {
        setRawData(null)
        return
      }
      const { subElements } = selectedElement
      setRawData({
        ...selectedElement,
        subElements: `${subElements?.length || 0} truncated`,
      })

      const actionsForElement = getSlsActionsByElementId(currentImage?.actions, elementId)
      const groupedActionsByTransactionId = actionsForElement.reduce((acc, actionForElement) => {
        const allActionsForTransactionId = [
          // eslint-disable-next-line no-unsafe-optional-chaining
          ...currentImage?.actions.filter((action) => action.transactionId === actionForElement.transactionId),
        ]
        if (!acc[actionForElement.transactionId]) {
          acc[actionForElement.transactionId] = allActionsForTransactionId
        }
        return acc
      }, {})
      setGroupedActions(groupedActionsByTransactionId)
      setSelectedActions(actionsForElement)
    },
    [currentImage]
  )

  // On initial mount, expand/focus the element if initialExpandElementId is provided
  useEffect(() => {
    if (initialExpandElementId && currentImage && !hasScrolledToInitial.current) {
      const elementPath = findElementPath(initialExpandElementId)
      if (!Array.isArray(elementPath) || elementPath.length === 0) {
        console.warn(`Element path not found for ID: ${initialExpandElementId}`)
        return
      }
      const pathTreeNodeKeys = []
      while (elementPath.length > 0) {
        pathTreeNodeKeys.push(elementPath.join(treeKeySep))
        elementPath.shift()
      }
      setExpandedKeys(pathTreeNodeKeys)
      setSelectedKeys([pathTreeNodeKeys[0]])
      getRawData(initialExpandElementId)
    }
  }, [initialExpandElementId, currentImage, findElementPath, getRawData])

  useEffect(() => {
    if (initialExpandElementId && selectedKeys.length > 0 && !hasScrolledToInitial.current) {
      const treeContainer = document.querySelector('.rc-tree')
      if (treeContainer) {
        const selectedNode = treeContainer.querySelector('.rc-tree-node-selected')
        if (selectedNode) {
          selectedNode.scrollIntoView({ behavior: 'smooth', block: 'center' })
          Promise.resolve().then(() => {
            hasScrolledToInitial.current = true
          })
        }
      }
    }
  }, [initialExpandElementId, selectedKeys])

  useEffect(() => {
    if (currentImage) {
      const currentHighlights = getHighlights({ element: currentImage })
      setImageHighlights(currentHighlights)
      setDisplayHighlights(currentHighlights, highlightsApi)
    }
  }, [currentImage, highlightsApi])

  // Expands the tree to find the desired node and selects the first instance of it
  const goToElement = (autosuggestElement) => {
    if (!autosuggestElement?.key) return
    const elementId = autosuggestElement.key
    setQuery({ elementId })

    // Generate all keys that need to be expanded: [fieldId||parentId||grandparentId, parentId||grandparentId, grandparentId]
    const elementPath = findElementPath(elementId)
    const pathTreeNodeKeys = []
    while (elementPath.length > 0) {
      pathTreeNodeKeys.push(elementPath.join(treeKeySep))
      elementPath.shift() // remove first element
    }

    setExpandedKeys([])
    setSelectedKeys([])
    setExpandedKeys(pathTreeNodeKeys)
    setSelectedKeys([pathTreeNodeKeys[0]])
    getRawData(elementId)
  }

  // Gets the field value to be displayed
  const getFieldValue = (element) => {
    let value = element.fieldType

    const mostRecentField = getMostRecentFieldValueInArray(element)
    const normalizedVal = mostRecentField?.normalizedValues?.[0]?.text
    const origVal = mostRecentField?.origValue?.text

    if (normalizedVal) {
      value = `${value} — Normalized: "${normalizedVal}"`
    } else if (origVal) {
      value = `${value} — Orig: "${origVal}"`
    }

    return value
  }

  const isDangling = useCallback(
    (element) => element?.partitionKey !== state.currentImageArk || element?.isDangling,
    [state.currentImageArk]
  )

  // Gets the value to be displayed as the title
  const getTitle = useCallback((element) => {
    let titleValue = ''
    if (element.elementType === 'PERSON') {
      titleValue = `${getPersonName({ person: element })} ${element.primary ? '(Principal})' : ''}`
    }
    if (element.elementType === 'RELATIONSHIP') titleValue = element.relType
    if (element.elementType === 'NAME') titleValue = element.nameType
    if (element.elementType === 'EVENT') titleValue = element.eventType
    if (element.elementType === 'FIELD') titleValue = getFieldValue(element)
    if (element.elementType === 'RECORD') titleValue = element.recordType
    if (titleValue) return `${element.elementType || ''} ${titleValue}`
    return element?.elementType || element?.fieldType || 'NO-TYPE'
  }, [])

  // A recursive function that finds the children of each parent and adds them to the `children` property
  const descendLevel = useCallback(
    (parent, parentIds) => {
      // Find elements that have the closest parent as one of their super(s)
      // const subs = elements.filter((element) => element.superElements?.some((s) => s.id === parentIds[0]))
      // Sort persons if the parent is a census record
      // eslint-disable-next-line no-warning-comments
      // parent.subElements?.sort((a) => (a.isShared ? -1 : 1)) // TODO: sort shared fields and structured subElements to the top.  Not working
      return parent?.subElements?.map((element) => {
        // const element = Object.keys(el).length < 3 ? elements.find((e) => e.id === el.id) || el : el
        const titleValue = getTitle(element)
        const isShared = element?.superElements?.length > 1
        const hasSubs = element?.subElements?.length > 0
        const sharedLabel = isShared ? ` [${element?.superElements?.length}x]` : ''
        const icon =
          (isShared && <NodeIconWrapper Icon={SocialShare} background={hasSubs ? colors.yellow03 : colors.blue03} />) ||
          (isDangling(element) && <NodeIconWrapper Icon={ContentDetach} background={colors.blue03} />)

        return {
          // Create globally unique key for shared fields by including parent IDs, such as 'fieldId||parentId||grandparentId'
          key: [element.id, ...parentIds].join(treeKeySep),
          title: `${titleValue}${sharedLabel}`,
          children: descendLevel(element, [element.id, ...parentIds]),
          icon,
        }
      })
    },
    [getTitle, isDangling]
  )

  // Gets the data to be used in the tree; starts with records and floating elements
  const treeData = useMemo(() => {
    if (!state.images || !currentImage) return []

    // 1. Records
    const recordElements = currentImage.subElements
    const recordsTreeData = recordElements?.map((record) => ({
      key: record.id,
      title: `${getTitle(record)}`,
      children: descendLevel(record, [record.id]),
    }))

    // 2. Floating elements
    const floatersTreeData = currentImage.floatingElements
      .filter((element) => element.elementType !== 'RECORD')
      .map((element) => {
        return {
          key: element.id,
          title: `${getTitle(element)}${isDangling(element) ? ' (DANGLING)' : ''}`,
          children: descendLevel(element, [element.id]),
          icon: (
            <NodeIconWrapper
              Icon={isDangling(element) ? TreeViewRelationship : ContentDetach}
              background={isDangling(element) ? colors.green05 : colors.blue03}
            />
          ),
        }
      })

    return recordsTreeData.concat(floatersTreeData)
  }, [currentImage, descendLevel, getTitle, isDangling, state.images])

  // Returns an array of all keys in a tree node’s children, recursively
  const findChildrenKeys = (node) => [node.key, ...node.children.flatMap(findChildrenKeys)]

  const toggleExpand = () => {
    // Expanding/collapsing can take a few seconds, so show a spinner
    setIsExpanding(true)
    setTimeout(() => {
      if (expandedKeys.length > 0) {
        setExpandedKeys([])
      } else {
        const treeRoot = { children: treeData }
        setExpandedKeys(findChildrenKeys(treeRoot))
      }
      setIsExpanding(false)
    }, 0)
  }

  const updateHighlights = (element) => {
    if (element?.elementType === 'FIELD') {
      const elementHighlights = imageHighlights.filter((h) => h.fieldId === element.id)
      if (elementHighlights) {
        setDisplayHighlights(elementHighlights, highlightsApi)
        handleSettingZoomPan(elementHighlights, setZoomPan)
      }
    }

    if (element?.elementType === 'PERSON') {
      const elementHighlights = imageHighlights.filter((highlight) => highlight.personIds.includes(element.personId))
      if (elementHighlights) {
        setDisplayHighlights(elementHighlights, highlightsApi)
        handleSettingZoomPan(elementHighlights, setZoomPan)
      }
    }

    if (element?.elementType === 'RECORD') {
      const elementHighlights = imageHighlights.filter((highlight) => highlight.recordId === element.recordId)
      if (elementHighlights) {
        setDisplayHighlights(elementHighlights, highlightsApi)
        handleSettingZoomPan(elementHighlights, setZoomPan)
      }
    }
  }

  // Selects a node and displays the raw data
  const selectNode = (selected) => {
    setSelectedKeys(selected)

    const elementId = selected[0].includes('|') ? selected[0].split('|')[0] : selected[0]
    setQuery({ elementId })
    const elements = getNestedOrFloatingSubelementsById(currentImage, elementId)
    const element = elements?.[0]

    updateHighlights(element)

    if (selected[0]) {
      getRawData(selected[0].split(treeKeySep)?.[0])
    } else {
      setRawData()
    }
  }
  // const personName = getPersonName({ person: currentImage?.floatingElements?.[0]})
  debug('ElementTreeView Render: ', {
    state,
    arkUrl: getImageArkUrl(state.currentImageArk, shared_dasProxy?.isOn),
    currentImageArk: state.currentImageArk,
    currentImage,
    autosuggestElements,
    autosuggestValues,
    expandedKeys,
    rawData,
    rawDataInfoSheet,
  })

  // TODO1: reset zoom after clicking a new element with useZoomPan's builtin functions

  return (
    <>
      <Separator size="xxs" />
      <H5>Element Tree</H5>
      <Paragraph size="sm">
        This is the list of sls elements put together in a format that is easily understood. You may filter this list of
        subelements by the id or the value of the field. You may need to scroll to see the highlighted field. The format
        follows this general structure: Image {`>`} Record {`>`} Person {`>`} Field/Event/Relationship.
      </Paragraph>
      <Separator />
      <DataBlock
        heading="Floating Elements:"
        data="elements that are not directly connected to a record and appear below
          without a folder."
        inline
      />
      <DataBlock
        heading="Dangling Elements:"
        data="elements that are not connected to the current image, but do have a record."
        inline
      />
      {collectionShowBanner && (
        <>
          <Separator size="sm" />
          <Alert type="help" dismissable onDismiss={collectionBannerProps.onDismiss}>
            {collectionBannerProps.message}
          </Alert>
        </>
      )}
      <Separator size="sm" />
      {error && <DebugErrorNotice errorLabel="Error" error={error} />}
      <Row wrap>
        <Autosuggest
          Icon={MenuSearch}
          data-testid="tree-view-field-value-search"
          suggestions={autosuggestValues}
          onSuggestionSelected={goToElement}
          label="Search for element by field value"
          placeholder="i.e. Braulia Ramonas or 13 de junio"
          virtualize
          growX
        />
        <Autosuggest
          Icon={MenuSearch}
          data-testid="tree-view-id-search"
          suggestions={autosuggestElements}
          onSuggestionSelected={goToElement}
          label="Search for element by ID"
          placeholder="i.e. a0de4529-82b1-4dcf-b06e-4e32b8b0d7a4"
          virtualize
          growX
        />
      </Row>
      <Button
        data-testid="tree-view-expand-collapse"
        onClick={toggleExpand}
        Icon={isExpanding ? NoticeLoading : undefined}
      >
        {expandedKeys.length > 0 ? 'Collapse All' : 'Expand All'}
      </Button>
      <div css={containerCss}>
        <Tree
          treeData={treeData}
          expandedKeys={expandedKeys}
          selectedKeys={selectedKeys}
          onSelect={selectNode}
          onExpand={setExpandedKeys}
        />
      </div>
      <InfoSheet
        title={
          <div css={imageContainerCSS}>
            <SingleImageViewer
              {...imageViewer}
              src={getImageArkUrl(state.currentImageArk, shared_dasProxy?.isOn)}
              alt="highlights preview for selected item"
            >
              <Highlights state={highlightsState} api={highlightsApi} Component={RecordHighlight} />
            </SingleImageViewer>
          </div>
        }
        {...rawDataInfoSheet}
        nonClosable
        size="lg"
      >
        <>
          <H5>Raw Data</H5>
          <Separator size="sm" />
          <ActionAndElementDetails
            groupedActions={groupedActions}
            rawData={rawData}
            fsUserAgentChain={fsUserAgentChain}
            selectedActions={selectedActions}
          />
        </>
      </InfoSheet>
    </>
  )
}

// Squish Zion icons to fit into the tree nodes
const nodeIconWrapperCss = css`
  span {
    position: relative;
    top: -3px;
    inset-inline-start: -4px;
    transform: scale(0.75);
    width: 23px !important; // this is necessary because the smallest zion icon is too big for this third-party tree
  }
`

const NodeIconWrapper = ({ Icon, ...iconProps }) => (
  <span css={nodeIconWrapperCss}>
    <Icon size="xs" {...iconProps} />
  </span>
)
